import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { LogIn, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  useDocumentTitle("Iniciar sesión");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policy, setPolicy] = useState({ text: "", version: null });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/policy").then((r) => setPolicy(r.data));
  }, []);

  const lastAccepted = (() => {
    const v = localStorage.getItem("last_accepted_policy_version");
    return v ? parseInt(v, 10) : null;
  })();
  const policyChanged =
    policy.version != null && lastAccepted != null && policy.version > lastAccepted;

  // Server-checked flag: `true` when the current email is a returning user
  // that has already accepted the version currently in force. The check runs
  // debounced from the email input (300 ms after the last keystroke) so we
  // don't hit the endpoint on every character. `null` means "not checked
  // yet"; we fall back to the localStorage heuristic while unknown.
  const [serverAccepted, setServerAccepted] = useState(null);

  useEffect(() => {
    // Reset when the email changes so we don't leak a stale "accepted" state
    // into a different account. The debounce below re-populates it.
    setServerAccepted(null);
    const em = email.trim().toLowerCase();
    if (!em || em.length < 4 || !em.includes("@")) return undefined;
    const handle = setTimeout(() => {
      api
        .post("/auth/consent-status", { email: em })
        .then((r) => setServerAccepted(!r.data?.needs_acceptance))
        .catch(() => setServerAccepted(null));
    }, 300);
    return () => clearTimeout(handle);
  }, [email]);

  // The user does not need to tick the checkbox ONLY when the server has
  // confirmed that THIS specific email already accepted the current version.
  // localStorage is device-level (a shared browser could have another user's
  // acceptance saved) so we deliberately do NOT use it to skip the checkbox
  // per typed email — that produced the "always enabled" regression.
  const alreadyAcceptedCurrent =
    policy.version != null && serverAccepted === true;

  useEffect(() => {
    if (alreadyAcceptedCurrent) setAccepted(true);
    else setAccepted(false);
  }, [alreadyAcceptedCurrent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Debes aceptar la política de privacidad y protección de datos para continuar.");
      return;
    }
    setLoading(true);
    try {
      const user = await login(
        email.trim().toLowerCase(),
        password,
        policy.version,
      );
      localStorage.setItem("last_accepted_policy_version", String(policy.version));
      toast.success(`Bienvenido, ${user.name}`);
      navigate(["gestion", "admin", "superadmin"].includes(user.role) ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand side */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-brand-soft relative overflow-hidden">
        <div className="flex flex-col items-start">
          <img
            src="/logo.jpg"
            alt="laclaseencasa"
            className="w-64 h-64 object-contain"
            style={{ mixBlendMode: "multiply" }}
          />
          <p className="text-[11px] text-brand-ink/70 -mt-10 ml-2 tracking-[0.18em] uppercase">
            de SIMA Asistencial
          </p>
          <a
            href="https://www.sima.madrid"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              window.open("https://www.sima.madrid", "_blank", "noopener,noreferrer");
            }}
            className="text-[11px] text-brand hover:text-brand-hover ml-2 mt-1 inline-flex items-center gap-1 font-mono-data tracking-wider transition-colors cursor-pointer"
            data-testid="sima-link-desktop"
          >
            www.sima.madrid
            <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="font-display text-4xl text-brand-ink leading-tight">
            Fórmate con SIMA. Aprende, trabaja, avanza.
          </p>
          <p className="text-base text-brand-ink/70 mt-3">
            Realiza los cursos asignados y sigue tu progreso curso a curso.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 bg-white relative">
        <Link
          to="/"
          className="absolute top-5 right-6 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-ink transition-colors font-mono-data tracking-wider"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Volver a la home
        </Link>
        <Card className="w-full max-w-md p-8 border border-gray-200 shadow-none rounded-lg">
          <div className="mb-8">
            <div className="lg:hidden flex flex-col items-center mb-6 text-center">
              <img
                src="/logo.jpg"
                alt="laclaseencasa"
                className="w-32 h-32 object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
              <p className="text-[10px] text-brand-ink/70 -mt-6 tracking-[0.18em] uppercase">
                de SIMA Asistencial
              </p>
              <a
                href="https://www.sima.madrid"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("https://www.sima.madrid", "_blank", "noopener,noreferrer");
                }}
                className="text-[10px] text-brand hover:text-brand-hover mt-1 inline-flex items-center gap-1 font-mono-data tracking-wider transition-colors cursor-pointer"
                data-testid="sima-link-mobile"
              >
                www.sima.madrid
                <span aria-hidden="true">↗</span>
              </a>
            </div>
            <h1 className="font-display text-3xl text-brand-ink">Iniciar sesión</h1>
            <p className="text-sm text-gray-500 mt-2">
              Introduce las credenciales que te ha proporcionado el administrador.
            </p>
            {policyChanged ? (
              <div
                className="mt-5 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm flex items-start gap-2"
                data-testid="policy-changed-banner"
              >
                <span className="text-base leading-none">⚠</span>
                <div className="flex-1">
                  <p className="font-medium">La política de privacidad ha cambiado</p>
                  <p className="text-xs mt-0.5">
                    Hemos publicado la versión <span className="font-mono-data">v{policy.version}</span>.{" "}
                    <button
                      type="button"
                      onClick={() => setPolicyOpen(true)}
                      className="underline underline-offset-2 hover:text-amber-700"
                    >
                      Léela
                    </button>{" "}
                    antes de aceptarla para continuar.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !accepted}
              className="w-full bg-brand hover:bg-brand-hover text-white active:scale-[0.98] transition-all py-6 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? "Entrando…" : "Entrar"}
            </Button>

            <div className="flex items-start gap-2 pt-2">
              {alreadyAcceptedCurrent ? (
                <p
                  className="text-[11px] text-gray-500 leading-relaxed font-normal"
                  data-testid="privacy-accepted-note"
                >
                  Ya aceptaste la política de privacidad
                  {policy.version ? (
                    <span className="text-[10px] text-gray-400 font-mono-data ml-1">
                      (v{policy.version})
                    </span>
                  ) : null}
                  .{" "}
                  <button
                    type="button"
                    onClick={() => setPolicyOpen(true)}
                    className="text-brand underline underline-offset-2 hover:text-brand-ink"
                    data-testid="privacy-link"
                  >
                    Consultarla
                  </button>
                </p>
              ) : (
                <>
                  <Checkbox
                    id="privacy"
                    checked={accepted}
                    onCheckedChange={(v) => setAccepted(v === true)}
                    className="mt-0.5"
                    data-testid="privacy-checkbox"
                  />
                  <Label htmlFor="privacy" className="text-xs text-gray-600 leading-relaxed font-normal cursor-pointer">
                    Acepto la{" "}
                    <button
                      type="button"
                      onClick={() => setPolicyOpen(true)}
                      className="text-brand underline underline-offset-2 hover:text-brand-ink"
                      data-testid="privacy-link"
                    >
                      política de privacidad y protección de datos
                    </button>
                    {policy.version ? (
                      <span className="text-[10px] text-gray-400 font-mono-data ml-1">
                        (v{policy.version})
                      </span>
                    ) : null}
                  </Label>
                </>
              )}
            </div>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <button
              type="button"
              onClick={() => setPolicyOpen(true)}
              className="text-xs text-gray-500 hover:text-brand-ink underline underline-offset-2"
              data-testid="footer-policy-link"
            >
              Política de privacidad y protección de datos
            </button>
          </div>
        </Card>
      </div>

      <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Política de privacidad y protección de datos
              {policy.version ? (
                <span className="text-xs font-normal text-gray-400 ml-2 font-mono-data">
                  v{policy.version}
                </span>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {policy.text || "Cargando…"}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
